import { Button, Card, Flex, Stack, Table, useTheme, spacing } from '@fti/design-system';
import { StatusBadge } from '../shared/StatusBadge';
import { ProgressBar } from '../shared/ProgressBar';
import type { Run } from '../../../types/media-sim';

interface RunListProps {
  runs: Run[];
  onSelect: (id: string) => void;
  onCreate: () => void;
}

export function RunList({ runs, onSelect, onCreate }: RunListProps) {
  const { theme } = useTheme();

  const columns = [
    {
      key: 'run_name',
      header: 'Run Name',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const run = row as unknown as Run;
        return (
          <button
            onClick={() => onSelect(run.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary[500],
              fontWeight: 500,
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {run.run_name || '(Untitled)'}
          </button>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (_val: unknown, row: Record<string, unknown>) => (
        <StatusBadge status={(row as unknown as Run).status} />
      ),
    },
    {
      key: 'message_count',
      header: 'Messages',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const run = row as unknown as Run;
        const count = run.message_count ?? 0;
        // The runs list only carries counts (not names); show a count tag, or
        // an "Event context" indicator when the run uses context instead.
        const label = count > 0
          ? `${count} message${count === 1 ? '' : 's'}`
          : run.event_context
            ? 'Event context'
            : '—';
        if (count === 0 && !run.event_context) {
          return <span style={{ color: theme.colors.text.secondary }}>—</span>;
        }
        return (
          <span
            style={{
              display: 'inline-block',
              padding: `2px ${spacing.xs}`,
              borderRadius: '10px',
              fontSize: '0.75rem',
              fontWeight: 500,
              backgroundColor: theme.colors.primary[100],
              color: theme.colors.primary[700],
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      key: 'publication_count',
      header: 'Publications',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const run = row as unknown as Run;
        return <span>{run.publication_count ?? 0}</span>;
      },
    },
    { key: 'number_of_articles', header: 'Articles' },
    {
      key: 'execution',
      header: 'Execution',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const run = row as unknown as Run;
        const isRunning = run.status.includes('running');
        if (run.status === 'draft') return null;
        return (
          <Flex align="center" gap="xs">
            <StatusBadge status={run.status} size="sm" />
            {isRunning && (
              <div style={{ width: 60 }}>
                <ProgressBar value={50} size="sm" color="primary" />
              </div>
            )}
          </Flex>
        );
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_val: unknown, row: Record<string, unknown>) =>
        new Date((row as unknown as Run).created_at).toLocaleDateString(),
    },
  ];

  return (
    <Stack gap="md">
      <Flex justify="end">
        <Button variant="solid" color="primary" onClick={onCreate}>
          Create Run
        </Button>
      </Flex>
      {runs.length === 0 ? (
        <Card variant="outlined">
          <Card.Body>
            <Flex justify="center" style={{ padding: spacing.lg }}>
              <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
                No runs yet. Create a run to configure a simulation.
              </p>
            </Flex>
          </Card.Body>
        </Card>
      ) : (
        <Table
          data={runs as unknown as Record<string, unknown>[]}
          columns={columns}
        />
      )}
    </Stack>
  );
}
